import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Typography,
  Box,
  Card,
  Grid,
  Button,
  Radio,
  FormControlLabel,
  RadioGroup,
  FormControl,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import useApiCaller from 'hooks/apiCaller';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';

const Questionnaire = () => {
  const params = useParams();
  const courseId = params.id;
  const uniqueCourseKeyInEmployeeCourses = params.uniqueCourseKeyInEmployeeCourses;
  const apiCaller = useApiCaller();
  const navigate = useNavigate();
  const [allQuestion, setAllQuestion] = useState([]);
  const [pageItemSize, setPageItemSize] = useState(0);
  const [selectedOption, setSelectedOption] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [quesNo, setQuesNo] = useState(10);

  // Multi-language question support
  const [selectedLang, setSelectedLang] = useState('en');
  const [availableQuestionLanguages, setAvailableQuestionLanguages] = useState([]);
  const [showLangModal, setShowLangModal] = useState(false);
  const questionRequestRef = useRef(null);

  const handlegetAllQuestion = async (lang = 'en', isInitialLoad = false) => {
    // Cancel any in-flight request to prevent a slow previous response
    // from overwriting the latest language selection
    if (questionRequestRef.current) {
      questionRequestRef.current.abort();
    }
    questionRequestRef.current = new AbortController();

    try {
      const response = await apiCaller.get('/user/getQuestion', {
        params: { courseId: courseId, lang },
        signal: questionRequestRef.current.signal,
      });
      if (response) {
        const langs = response?.data?.data?.data?.availableQuestionLanguages || [];
        setAllQuestion(response?.data?.data?.data?.encryptData);
        setQuesNo(response?.data?.data?.data?.encryptData.length);
        setAvailableQuestionLanguages(langs);
        // Show language selection modal on first load when multiple languages exist
        if (isInitialLoad && langs.length > 1) {
          setShowLangModal(true);
        }
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return;
      console.log(error);
    }
  };

  const handleShowNextQuestion = () => {
    setPageItemSize((prev) => prev + 2);
  };

  const handleShowPrevQuestion = () => {
    setPageItemSize((prev) => prev - 2);
  };

  const handleSelectAnswer = (e, quesNo) => {
    const value = e.target.value;
    setSelectedOption((prev) => ({ ...prev, [quesNo]: value }));
  };

  const handleLanguageChange = (langCode) => {
    setSelectedLang(langCode);
    setSelectedOption({});
    setPageItemSize(0);
    setShowLangModal(false);
    handlegetAllQuestion(langCode);
  };

  const handleSubmitTest = async () => {
    setIsLoading(true);
    const data = {
      answer: selectedOption,
      courseId: courseId,
      uniqueCourseKeyInEmployeeCourses: uniqueCourseKeyInEmployeeCourses,
      lang: selectedLang,
    };
    try {
      const response = await apiCaller.post('/user/testSubmission', data);
      if (response) {
        setIsLoading(false);
        toast.success('Test Submitted Successful!', {
          theme: 'colored',
        });
        navigate(`/congratulation/${courseId}/${uniqueCourseKeyInEmployeeCourses}`);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(error?.response?.data?.message, {
        theme: 'colored',
      });
    }
  };

  useEffect(() => {
    handlegetAllQuestion('en', true);
  }, []);

  return (
    <Layout>
      <Box className="p-3" sx={{ marginLeft: 2 }}>
        <Typography sx={{ fontSize: '20px', marginBottom: 3 }}>Questionnaire</Typography>

        {/* Language selector - only shown if multiple question languages are available */}
        {availableQuestionLanguages.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Question Language:</Typography>
            {availableQuestionLanguages.map((ql) => (
              <button
                key={ql.code}
                onClick={() => handleLanguageChange(ql.code)}
                style={{
                  padding: '6px 14px',
                  border: selectedLang === ql.code ? '2px solid #2800B1' : '1px solid #ccc',
                  background: selectedLang === ql.code ? '#e7f1ff' : 'white',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: selectedLang === ql.code ? 700 : 400,
                }}
              >
                {ql.name.toUpperCase()}
              </button>
            ))}
          </Box>
        )}

        {allQuestion.slice(pageItemSize, pageItemSize + 2).map((val, index) => {
          return (
            <Box sx={{ marginBottom: 2 }} key={index}>
              <Card sx={{ minWidth: 275 }} className="p-3">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 1,
                  }}
                >
                  <Box sx={{ fontWeight: 500, fontSize: 20 }}>Question {val.quesNo}</Box>
                  <Box sx={{ fontWeight: 400, fontSize: 20 }}>
                    {val.quesNo} of {quesNo} Questions
                  </Box>
                </Box>

                <Box sx={{ fontSize: 18, fontWeight: 400 }}>{val.question}</Box>
                <Box>
                  <Box sx={{ marginTop: 1 }}>
                    <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                      {val?.options?.map((item, index) => (
                        <Grid item xs={2} sm={6} md={6} key={index}>
                          <button
                            style={{
                              border: 'none',
                              width: '80%',
                              padding: 6,
                              boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
                              background: 'white',
                              borderRadius: 4,
                              display: 'flex',
                              paddingLeft: '2%',
                            }}
                          >
                            <FormControl>
                              <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                name="radio-buttons-group"
                                onChange={(e) => handleSelectAnswer(e, val.quesNo)}
                              >
                                <FormControlLabel
                                  value={item}
                                  control={<Radio />}
                                  label={item}
                                  checked={selectedOption[val.quesNo] == item}
                                />
                              </RadioGroup>
                            </FormControl>
                          </button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              </Card>
            </Box>
          );
        })}

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ marginRight: 2 }}>
            <Button
              variant="outlined "
              size="medium"
              sx={{
                borderRadius: 50,
                fontSize: 12,
                border: '1px solid #2800B1',
                color: '#706C84',
                width: 200,
              }}
              onClick={handleShowPrevQuestion}
              disabled={pageItemSize == 0}
            >
              Previous
            </Button>
          </Box>
          <Box>
            {allQuestion.slice(pageItemSize, pageItemSize + 2).filter((val) => val.quesNo == quesNo)
              .length ? (
              <Button
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 50,
                  background: '#2800B1',
                  fontSize: 12,
                  border: '1px solid #2800B1',
                  width: 200,
                }}
                onClick={handleSubmitTest}
              >
                {isLoading ? <CircularProgress size={23} color="inherit" /> : 'Submit'}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 50,
                  background: '#2800B1',
                  fontSize: 12,
                  border: '1px solid #2800B1',
                  width: 200,
                }}
                onClick={handleShowNextQuestion}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Question Language Selection Modal */}
      <Dialog open={showLangModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 1 }}>
          Select Question Language
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Questions are available in multiple languages. Choose the language you'd like to answer
            in.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {availableQuestionLanguages.map((lang) => (
              <Button
                key={lang.code}
                variant="outlined"
                onClick={() => handleLanguageChange(lang.code)}
                sx={{
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  color: '#1e293b',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  '&:hover': {
                    background: '#2800B1',
                    color: '#fff',
                    borderColor: '#2800B1',
                  },
                }}
              >
                {lang.name}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="text"
            onClick={() => setShowLangModal(false)}
            sx={{ color: '#64748b', textTransform: 'none' }}
          >
            Continue with English
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Questionnaire;
